import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/src/shadComponents/ui/dialog';
import { Button } from '@/src/shadComponents/ui/button';
import { Checkbox } from '@/src/shadComponents/ui/checkbox';
import { Skeleton } from '@/src/shadComponents/ui/skeleton';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import { Search } from 'lucide-react';

const RENDER_BATCH = 50;

interface Activation {
    id: number;
    name: string;
    date: string;
    location: string;
    isArchived: boolean;
}

interface ActivationPickerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedIds: number[];
    onSave: (ids: number[]) => void;
}

export default function ActivationPickerDialog({
    open,
    onOpenChange,
    selectedIds,
    onSave,
}: ActivationPickerDialogProps) {
    const { getActivations } = useActivations();
    const [activations, setActivations] = useState<Activation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Set<number>>(new Set(selectedIds));
    const [visibleCount, setVisibleCount] = useState(RENDER_BATCH);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        setSelected(new Set(selectedIds));
        setSearch('');
        setVisibleCount(RENDER_BATCH);

        let isMounted = true;
        const fetch = async () => {
            setIsLoading(true);
            try {
                const data = await getActivations();
                if (isMounted) {
                    setActivations(
                        data
                            .filter((a: Activation) => !a.isArchived)
                            .sort(
                                (a: Activation, b: Activation) =>
                                    new Date(b.date).getTime() -
                                    new Date(a.date).getTime(),
                            ),
                    );
                }
            } catch (err) {
                console.error('Failed to load activations', err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetch();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const filtered = useMemo(() => {
        if (!search) return activations;
        const lower = search.toLowerCase();
        return activations.filter((a) =>
            a.name.toLowerCase().includes(lower),
        );
    }, [activations, search]);

    // Reset visible count when search changes
    useEffect(() => {
        setVisibleCount(RENDER_BATCH);
    }, [search]);

    const visible = filtered.slice(0, visibleCount);
    const hasMore = visibleCount < filtered.length;

    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el || !hasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            setVisibleCount((prev) => prev + RENDER_BATCH);
        }
    }, [hasMore]);

    const toggle = (id: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSave = () => {
        onSave(Array.from(selected));
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Select Activations</DialogTitle>
                    <DialogDescription>
                        Choose which activations to monitor on your Command
                        Center.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2 rounded-md border border-input px-3 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                    <Search className="size-4 shrink-0 text-muted-foreground" />
                    <input
                        placeholder="Search activations..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                </div>

                <div
                    ref={listRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto min-h-0 max-h-[400px] border rounded-md"
                >
                    {isLoading ? (
                        <div className="p-4 space-y-3">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Skeleton key={i} className="h-8 w-full" />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">
                            {search
                                ? 'No activations match your search.'
                                : 'No activations found.'}
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {visible.map((activation) => (
                                <label
                                    key={activation.id}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer"
                                >
                                    <Checkbox
                                        checked={selected.has(activation.id)}
                                        onCheckedChange={() =>
                                            toggle(activation.id)
                                        }
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                            {activation.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {activation.location} &middot;{' '}
                                            {activation.date?.split('T')[0]}
                                        </p>
                                    </div>
                                </label>
                            ))}
                            {hasMore && (
                                <div className="px-4 py-3 text-center text-xs text-muted-foreground">
                                    Showing {visible.length} of{' '}
                                    {filtered.length} &mdash; scroll for more
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="text-xs text-muted-foreground">
                    {selected.size} activation
                    {selected.size !== 1 ? 's' : ''} selected
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
